import { captureSubmitError } from './captureSubmitError'
import { captureEvent } from '../metrics/captureEvent'
import { captureSubmitPerformance } from './captureSubmitPerformance'
import { getGACID } from '../default/getGACIID'
import { getFunnelFromUrl } from '../default/getFunnelFromUrl'
import { getTrid } from '../default/getTrid'
import { firePixels } from '../default/firePixels'
import { formSubmit } from '../middleware/formSubmit'
import { getIds } from '../default/getIds'
import { getFunnelCache } from '../default/getFunnelCache'
import { getAbTestPayloadString } from '../default/getAbTestPayload'
import { gtagsConversion } from '../pixels/gtags'

/**
 *
 * @param {FunnelMainSubmitData} detail
 * @param {string} sessionID
 * @returns {Promise<string>}
 */
export async function submitForm(detail, sessionID = 'tdMainFunnel') {
  const startTime = performance.now()
  let redirectUrl = ''

  try {
    const sessionCache = getFunnelCache(sessionID)

    const { externalID, uniqueID } = getIds()

    const firstName =
      detail.firstName ||
      (sessionCache?.name ? sessionCache.name.split(' ')[0] : '')
    let lastName = detail.lastName || ''
    if (!lastName && (detail.name || sessionCache?.name)) {
      const fullName = detail.name || sessionCache?.name || ''
      const nameParts = fullName.split(' ')
      if (nameParts.length > 2) {
        lastName = nameParts.slice(1).join(' ')
      } else {
        lastName = nameParts[1] || ''
      }
    }

    const name =
      (detail.name || '').trim() ||
      `${firstName} ${lastName}`.trim() ||
      sessionCache?.name
    const addressLine =
      (detail.addressLine?.split(',')[0] || '').trim() ||
      detail.addressLine ||
      sessionCache?.addressLine ||
      ''

    /** @type {SubmitBody} */
    const submitBody = {
      // referer
      referrer: sessionCache?.referrer || '',

      // Ugly Capitalized :c
      DebtOwed: parseInt(detail.debtOwed || sessionCache?.debtOwed || '0'),
      Email: detail.email || sessionCache?.email || '',
      Name: name || '',
      Phone: detail.phone || sessionCache?.phone || '',
      State:
        detail.state ||
        sessionCache?.state ||
        sessionCache?.mainState ||
        sessionCache?.region ||
        '',

      // User data
      address: addressLine || undefined,
      address1: addressLine || undefined,
      birthdate: detail.birthdate || sessionCache?.birthdate || '',
      city: detail.city || sessionCache?.city || undefined,
      email: detail.email || sessionCache?.email || '',
      first: firstName,
      last: lastName,
      name: name || '',
      phone: detail.phone || sessionCache?.phone || '',
      ssn: detail.ssn || undefined,
      ssnEncrypted: detail.ssnEncrypted || undefined,
      state: detail.state || sessionCache?.state || sessionCache?.region || '',
      zip_code: detail.zipcode || sessionCache?.zipcode || undefined, // snakecase :c

      // Location data
      ip: sessionCache?.ip || '::1',
      remoteAddress: sessionCache?.ip || '::1',
      userAgent: window?.navigator?.userAgent || '',

      // Cert
      // xxTrustedFormCertUrl: trustedFormData.xxTrustedFormCertUrl || '', // can also use this
      xxTrustedFormCertUrl:
        detail.xxTrustedFormCertUrl || sessionCache?.xxTrustedFormCertUrl || '',

      // Marketing
      eventID: uniqueID,
      external_ID: externalID,
      hide_pixels: sessionCache?.utm_campaign ? 0 : 1,
      gaId: getGACID() || '',
      fbclid: sessionCache?.fbc || '', //named as fbclid in database but actually fbc
      fbp: sessionCache?.fbp || '',

      funnel: sessionCache?.funnel || getFunnelFromUrl() || 'unknown',
      is_des: detail.is_des || undefined,
      ab_test: getAbTestPayloadString(sessionCache),
      unique_session_uuid: sessionCache?.unique_session_id || '',

      // From query params
      aff_id: sessionCache?.aff_id || sessionCache?.utm_campaign || '',
      affid2: sessionCache?.affid2 || '',
      oid: sessionCache?.oid || '',
      oid2: sessionCache?.oid2 || '',
      sourceid: sessionCache?.sourceid || '',
      sub1: sessionCache?.sub1 || '',
      sub2: sessionCache?.sub2 || '',
      sub3: sessionCache?.sub3 || '',
      sub4: sessionCache?.sub4 || '',
      sub5: sessionCache?.sub5 || '',
      test_pixel: sessionCache?.test_pixel || undefined,
      trid: sessionCache?.trid || '',
      utm_campaign: sessionCache?.utm_campaign || sessionCache?.aff_id || '',
      utm_source: sessionCache?.utm_source || '',
      website: sessionCache?.website || getFunnelFromUrl() || 'unknown',

      // No idea :*
      pval: sessionCache?.pval || '',
      lc_pop: 0,
    }

    /**
     * SessionLogic component migration
     * -----------------
     */
    // Should probably migrate this to backend
    const requiredTridAffiliates = ['22', '570', '892']
    if (requiredTridAffiliates.includes(`${submitBody.utm_campaign}`)) {
      try {
        const trid = await getTrid({
          affId2: submitBody.affid2,
          oid2: submitBody.oid2,
          sourceId: submitBody.sourceid,
          sub1: submitBody.sub1,
          sub2: submitBody.sub2,
          sub3: submitBody.sub3,
          sub4: submitBody.sub4,
          sub5: submitBody.sub5,
          utmCampaign: `${submitBody.utm_campaign}`,
        })
        submitBody.trid = trid
      } catch (error) {
        captureSubmitError(/** @type {Error} */ (error), false)
      }
    }

    if (sessionCache?.utm_source === 'google') {
      if (sessionCache?.sub1 === 'gmb') {
        submitBody.sub1 = 'Google MyBusiness Listing'
        submitBody.utm_campaign = 1010101
        submitBody.sub2 = sessionCache?.referrer || ''
      } else {
        submitBody.utm_campaign = 9991
        submitBody.sub1 = sessionCache?.utm_campaign || ''
        submitBody.sub2 = sessionCache?.utm_content || ''
        submitBody.sub3 = sessionCache?.utm_term || ''
        if (submitBody.sub1 === '') {
          submitBody.sub1 = sessionCache?.campId || ''
          submitBody.sub2 = sessionCache?.agId || ''
          submitBody.sub3 = sessionCache?.utm_term || ''
        }
      }
    }

    if (['td_cars', 'propel'].includes(sessionCache?.utm_source || '')) {
      submitBody.utm_campaign =
        sessionCache?.utm_source === 'propel' ? 13 : 10000

      submitBody.sub1 = `${sessionCache?.utm_campaign}` || ''
      submitBody.sub2 = sessionCache?.utm_content || ''
      submitBody.sub3 = sessionCache?.utm_term || ''
    }

    if (sessionCache?.rId) {
      submitBody.utm_campaign = 10005
      submitBody.sub1 = sessionCache.rId
      submitBody.sub2 = sessionCache?.utm_content || ''
      submitBody.sub3 = sessionCache?.utm_term || ''
    }

    // TODO: If no utm_campaign should not send sub{1...},
    // utm_source, etc..
    // or any of the other marketing params as
    // SessionLogic component does
    // but let's see if this works
    // also best solution is to have backend handle it
    if (!submitBody.utm_campaign) {
      if (!submitBody.referrer) {
        submitBody.utm_campaign = 0
        submitBody.sub1 = 'SEO - Branded'
      } else {
        submitBody.utm_campaign = 1010101
        submitBody.sub1 = submitBody.referrer
      }
    }

    const data = await formSubmit(submitBody)
    if (Object.keys(data).length === 0) {
      throw new Error('No data returned from formSubmit')
    }

    redirectUrl = data.return_link || ''

    if (!redirectUrl) {
      captureEvent('no-redirect-url-returned-by-be')
    }

    if (
      !['getting-started', 'onboarding-savings-v3', 'ps-onboarding'].includes(
        submitBody.funnel,
      )
    ) {
      try {
        await firePixels(data, submitBody)
        // internal
        if (submitBody.utm_campaign === '918') {
          await gtagsConversion('G-MC0PZ596KV', {
            send_to: 'AW-11424527567/0HiJCMiNoZYZEM_p0ccq',
          })
        }
      } catch (error) {
        captureSubmitError(/** @type {Error} */ (error), false)
      }
    }
  } catch (error) {
    captureSubmitError(/** @type {Error} */ (error), false)
    throw error
  } finally {
    captureSubmitPerformance(performance.now() - startTime)
  }

  return redirectUrl
}
